import React from "react";
import styled, { css } from "styled-components";
import { FiCheck, FiImage, FiPlus, FiX } from "react-icons/fi";
import { Action, FormField, Select } from "../shared";
import { useLanguage } from "../../../contexts/LanguageContext";
import ItemImage from "./ItemImage";
import {
  Item,
  TEAM_CT,
  TEAM_T,
  decorationName,
  rarityKey,
  splitName,
} from "./data";

const compactPreview = css`
  grid-template-columns: 96px minmax(0, 1fr);
  justify-items: start;
  text-align: left;
  align-items: center;
  gap: 6px 14px;
  .stage {
    grid-row: 1 / span 2;
    padding: 2px 0;
    .img {
      width: 100%;
    }
  }
  .names {
    justify-items: start;
  }
  .skin {
    font-size: 16px;
    line-height: 21px;
  }
  .chips {
    justify-content: flex-start;
  }
`;

const Preview = styled.div`
  display: grid;
  gap: 10px;
  justify-items: center;
  text-align: center;
  padding: 10px 10px 12px;
  border-radius: 14px;
  background: ${(p) => p.theme.colors.navigation};
  box-shadow: inset 0 0 0 1px ${(p) => p.theme.colors.border};
  .stage {
    width: 100%;
    display: grid;
    place-items: center;
    padding: 6px 0;
    border-radius: 10px;
    background:
      linear-gradient(to top, color-mix(in srgb, var(--rarity, #65778d) 18%, transparent), transparent 60%),
      radial-gradient(
        120% 90% at 50% 20%,
        ${(p) => p.theme.colors.surfaceHover},
        ${(p) => p.theme.colors.navigation}
      );
    box-shadow: inset 0 -3px 0 var(--rarity, #65778d);
    .img {
      width: min(100%, 240px, calc(21vh * 1.333));
      aspect-ratio: 4 / 3;
    }
    img {
      filter: drop-shadow(0 8px 10px #0007);
    }
  }
  .names {
    display: grid;
    gap: 1px;
    justify-items: center;
    min-width: 0;
    max-width: 100%;
  }
  .rarity {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 12px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.textMuted};
    &::before {
      content: "";
      width: 8px;
      height: 8px;
      border-radius: 2px;
      background: var(--rarity, #65778d);
    }
  }
  .skin {
    font-size: 19px;
    line-height: 25px;
    font-weight: 750;
    letter-spacing: -0.02em;
    overflow-wrap: anywhere;
  }
  .model {
    font-size: 13px;
    color: ${(p) => p.theme.colors.textSecondary};
    overflow-wrap: anywhere;
    .wear {
      color: ${(p) => p.theme.colors.textMuted};
    }
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    min-height: 26px;
    padding: 0 9px 0 7px;
    border-radius: 7px;
    font-size: 12px;
    font-weight: 650;
    color: ${(p) => p.theme.colors.text};
    background: ${(p) => p.theme.colors.input};
    box-shadow: inset 0 0 0 1px ${(p) => p.theme.colors.border};
    svg {
      width: 13px;
      height: 13px;
      color: ${(p) => p.theme.colors.primary};
    }
    &.off {
      padding-left: 9px;
      color: ${(p) => p.theme.colors.textMuted};
      font-weight: 550;
    }
  }
  @media (min-width: 861px) and (max-height: 820px) {
    ${compactPreview}
    padding: 10px 12px;
  }
  @media (max-width: 860px) {
    grid-area: preview;
    ${compactPreview}
    padding: 10px 14px;
    border-radius: 0;
    box-shadow: none;
    border-bottom: 1px solid ${(p) => p.theme.colors.border};
  }
`;

export function SkinPreview({
  item,
  teams,
  wear,
}: {
  item: Item;
  teams: number[];
  wear?: string;
}) {
  const { t } = useLanguage();
  const { model, skin } = splitName(item.name);
  const rk = rarityKey(item);
  const sides = [TEAM_T, TEAM_CT].filter((x) => teams.includes(x));
  return (
    <Preview style={{ "--rarity": item.color } as React.CSSProperties}>
      <div className="stage">
        <div className="img">
          <ItemImage src={item.image} alt={item.name} />
        </div>
      </div>
      <div className="names">
        {rk && <span className="rarity">{t(rk)}</span>}
        <div className="skin">{skin || model}</div>
        <div className="model">
          {skin ? model : ""}
          {wear ? <span className="wear">{skin ? " · " : ""}{t(`skins.wear.${wear}`)}</span> : null}
        </div>
      </div>
      <div className="chips">
        {sides.length === 0 ? (
          <span className="chip off">{t("skins.status_none")}</span>
        ) : (
          sides.map((x) => (
            <span key={x} className="chip">
              <FiCheck aria-hidden="true" />
              {t(x === TEAM_T ? "skins.status_t" : "skins.status_ct")}
            </span>
          ))
        )}
      </div>
    </Preview>
  );
}

const Settings = styled.div`
  display: grid;
  align-content: start;
  gap: 20px;
  min-width: 0;
  h3.title {
    font-size: 13px;
    line-height: 18px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${(p) => p.theme.colors.textMuted};
  }
  .note {
    font-size: 12px;
    line-height: 17px;
    color: ${(p) => p.theme.colors.textMuted};
  }
  .wear select {
    min-height: 46px;
    padding-block: 9px;
    font-size: 15px;
  }
`;
const SideList = styled.fieldset`
  display: grid;
  gap: 8px;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
  legend {
    padding: 0;
    margin-bottom: 8px;
    font-size: 13px;
    line-height: 18px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${(p) => p.theme.colors.textMuted};
  }
  .alert {
    font-size: 13px;
    color: ${(p) => p.theme.colors.primary};
  }
`;
const SideRow = styled.div<{ $on: boolean; $off?: boolean }>`
  display: grid;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 12px;
  background: ${(p) => (p.$on ? p.theme.colors.primarySoft : p.theme.colors.input)};
  box-shadow: inset 0 0 0 1px ${(p) => (p.$on ? "#ff9b5740" : p.theme.colors.border)};
  opacity: ${(p) => (p.$off ? 0.55 : 1)};
  .line {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }
  .badge {
    flex: none;
    display: grid;
    place-items: center;
    min-width: 34px;
    height: 26px;
    padding: 0 6px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 800;
    background: ${(p) => p.theme.colors.navigation};
    color: ${(p) => (p.$on ? p.theme.colors.primary : p.theme.colors.textSecondary)};
    box-shadow: inset 0 0 0 1px ${(p) => (p.$on ? "#ff9b5755" : p.theme.colors.border)};
  }
  .who {
    display: grid;
    min-width: 0;
    flex: 1 1 auto;
    strong {
      font-size: 14px;
      line-height: 18px;
    }
    span {
      font-size: 12px;
      line-height: 16px;
      color: ${(p) => (p.$on ? p.theme.colors.primary : p.theme.colors.textMuted)};
      font-weight: 600;
    }
  }
  button.act {
    flex: none;
    min-width: 120px;
    min-height: 36px;
    padding: 6px 10px;
    font-size: 13px;
  }
  .diff {
    font-size: 12px;
    line-height: 17px;
    color: ${(p) => p.theme.colors.textSecondary};
    strong {
      color: ${(p) => p.theme.colors.text};
      font-weight: 650;
      overflow-wrap: anywhere;
    }
    &.will {
      color: ${(p) => p.theme.colors.primary};
    }
  }
`;

interface SettingsProps {
  item: Item;
  isNew: boolean;
  teams: number[];
  savedTeams: number[];
  onTeams: (teams: number[]) => void;
  /** Предмет, который сейчас стоит на стороне (кроме редактируемого). */
  occupant: Record<number, string>;
  wear: string;
  wearOptions: string[];
  onWear: (wear: string) => void;
  busy?: boolean;
}

/** Состояние и экипировка по сторонам: изменения попадают в черновик. */
export function SkinSettings({
  item,
  isNew,
  teams: chosen,
  savedTeams,
  onTeams,
  occupant,
  wear,
  wearOptions,
  onWear,
  busy,
}: SettingsProps) {
  const { t } = useLanguage();
  const allowed = item.allowedTeams || [];
  const toggle = (team: number) =>
    onTeams(chosen.includes(team) ? chosen.filter((x) => x !== team) : [...chosen, team]);
  const known = wearOptions.includes(wear);
  return (
    <Settings>
      {wearOptions.length > 0 && (
        <FormField className="wear">
          {t("skins.wear")}
          <Select value={known ? wear : ""} disabled={busy} onChange={(e) => onWear(e.target.value)}>
            {!known && (
              <option value="" disabled>
                {t("skins.wear_unset")}
              </option>
            )}
            {wearOptions.map((w) => (
              <option key={w} value={w}>
                {t(`skins.wear.${w}`)}
              </option>
            ))}
          </Select>
        </FormField>
      )}

      <SideList>
        <legend>{t("skins.equip_menu")}</legend>
        {[TEAM_T, TEAM_CT].map((team) => {
          const side = team === TEAM_T ? "T" : "CT";
          const ok = allowed.includes(team);
          const on = chosen.includes(team);
          const other = occupant[team];
          const changed = on !== savedTeams.includes(team);
          return (
            <SideRow key={team} $on={on} $off={!ok}>
              <div className="line">
                <span className="badge">{side}</span>
                <span className="who">
                  <strong>{t(team === TEAM_T ? "skins.side_t_short" : "skins.side_ct_short")}</strong>
                  <span>
                    {ok
                      ? on
                        ? t("skins.equip_this", { side })
                        : t("skins.status_none")
                      : t("skins.equip_incompatible", { side })}
                    {ok && changed ? ` · ${t("skins.equip_draft_note")}` : ""}
                  </span>
                </span>
                {ok && (
                  <Action
                    type="button"
                    className="act"
                    $variant={on ? undefined : "primary"}
                    aria-pressed={on}
                    disabled={busy}
                    onClick={() => toggle(team)}
                  >
                    {t(on ? "skins.unequip_for" : "skins.equip_for", { side })}
                  </Action>
                )}
              </div>
              {ok && other && (
                <div className={on ? "diff will" : "diff"}>
                  {on ? (
                    t("skins.draft_replaces", { side, name: other })
                  ) : (
                    <>
                      {t("skins.equip_now", { side, name: "" })}
                      <strong>{other}</strong>
                    </>
                  )}
                </div>
              )}
            </SideRow>
          );
        })}
        {isNew && chosen.length === 0 && (
          <span role="alert" className="alert">
            {t("skins.side_required")}
          </span>
        )}
      </SideList>
    </Settings>
  );
}

/* ---------- Слоты украшений ---------- */

const Slots = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr)) 1px minmax(0, 1.05fr);
  gap: 10px;
  align-items: stretch;
  .sep {
    background: ${(p) => p.theme.colors.border};
    margin: 6px 0;
  }
  @media (max-width: 600px) {
    gap: 6px;
  }
`;
const Slot = styled.div<{ $filled: boolean; $active: boolean }>`
  position: relative;
  button.main {
    width: 100%;
    height: 100%;
    min-height: 92px;
    display: grid;
    gap: 4px;
    justify-items: center;
    align-content: center;
    padding: 8px 4px;
    border-radius: 12px;
    box-shadow: ${(p) =>
      p.$active
        ? `inset 0 0 0 2px ${p.theme.colors.primary}, 0 0 0 4px ${p.theme.colors.primarySoft}`
        : `inset 0 0 0 1px ${p.$filled ? p.theme.colors.borderLight : p.theme.colors.border}`};
    background: ${(p) => (p.$filled ? p.theme.colors.raised : p.theme.colors.navigation)};
    color: ${(p) => (p.$active ? p.theme.colors.text : p.theme.colors.textSecondary)};
    font-size: 12px;
    transition:
      box-shadow ${(p) => p.theme.motion.fast},
      background ${(p) => p.theme.motion.fast};
    &:hover:not(:disabled) {
      box-shadow: inset 0 0 0 1px ${(p) => p.theme.colors.primary};
      ${(p) => p.$active && `box-shadow: inset 0 0 0 2px ${p.theme.colors.primary}, 0 0 0 4px ${p.theme.colors.primarySoft};`}
    }
    .thumb {
      width: 52px;
      height: 40px;
      display: grid;
      place-items: center;
      color: ${(p) => p.theme.colors.textMuted};
      svg {
        width: 18px;
        height: 18px;
      }
    }
    .label {
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-weight: ${(p) => (p.$active ? 700 : 500)};
    }
    .state {
      font-size: 10px;
      line-height: 12px;
      font-weight: 800;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: ${(p) => p.theme.colors.primary};
    }
  }
  button.remove {
    position: absolute;
    top: -7px;
    right: -7px;
    width: 26px;
    height: 26px;
    display: grid;
    place-items: center;
    border-radius: 999px;
    background: ${(p) => p.theme.colors.surfaceHover};
    border: 1px solid ${(p) => p.theme.colors.borderLight};
    color: ${(p) => p.theme.colors.textSecondary};
    &:hover {
      color: ${(p) => p.theme.colors.danger};
      border-color: ${(p) => p.theme.colors.danger};
    }
  }
  @media (min-width: 861px) and (max-height: 820px) {
    button.main {
      min-height: 76px;
      padding: 6px 4px;
      .thumb {
        height: 32px;
      }
    }
  }
  @media (max-width: 600px) {
    button.main {
      min-height: 72px;
      padding: 6px 2px;
      .thumb {
        width: 100%;
        height: 32px;
      }
      .label {
        font-size: 11px;
      }
      .state {
        display: none;
      }
    }
  }
`;
const Context = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  min-height: 54px;
  padding: 10px 0 12px;
  h3 {
    font-size: 16px;
    line-height: 22px;
    font-weight: 700;
  }
  .sub {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    font-size: 13px;
    line-height: 18px;
    color: ${(p) => p.theme.colors.textMuted};
    .cur {
      flex: none;
      width: 32px;
      height: 26px;
      border-radius: 6px;
      background: ${(p) => p.theme.colors.navigation};
      box-shadow: inset 0 0 0 1px ${(p) => p.theme.colors.border};
    }
    strong {
      color: ${(p) => p.theme.colors.text};
      font-weight: 650;
    }
  }
  .text {
    display: grid;
    gap: 2px;
    min-width: 0;
  }
  button.clear {
    flex: none;
    min-height: 38px;
    padding: 6px 12px;
    font-size: 13px;
  }
  @media (min-width: 861px) and (max-height: 820px) {
    min-height: 0;
    padding: 8px 0 8px;
    .sub {
      display: none;
    }
  }
  @media (max-width: 600px) {
    flex-wrap: nowrap;
    min-height: 0;
    padding: 10px 0 8px;
    h3 {
      font-size: 15px;
    }
    .sub {
      display: none;
    }
    button.clear {
      min-height: 34px;
      padding: 4px 10px;
    }
  }
`;

export type SlotPick = number | "charm";

interface DecoProps {
  stickers: string[];
  charm: string;
  decorations: Record<string, Item>;
  active: SlotPick;
  onPick: (slot: SlotPick) => void;
  onClearSticker: (slot: number) => void;
  onClearCharm: () => void;
  busy?: boolean;
}

/** Слоты наклеек и брелка плюс строка контекста выбранного слота. */
export function DecorationSlots({
  stickers,
  charm,
  decorations,
  active,
  onPick,
  onClearSticker,
  onClearCharm,
  busy,
}: DecoProps) {
  const { t } = useLanguage();
  const activeId = active === "charm" ? charm : stickers[active];
  const activeName = decorationName(activeId ? decorations[activeId] : undefined);
  const ctxTitle =
    active === "charm" ? t("skins.pick_charm") : t("skins.pick_sticker", { n: active + 1 });
  return (
    <section aria-label={t("skins.decorations")}>
      <Slots>
        {[0, 1, 2, 3, 4].map((slot) => {
          const id = stickers[slot];
          const deco = id ? decorations[id] : undefined;
          const name = decorationName(deco);
          const on = active === slot;
          return (
            <Slot key={slot} $filled={!!id} $active={on}>
              <button
                type="button"
                className="main"
                data-slot={slot}
                disabled={busy}
                aria-label={
                  id
                    ? t("skins.slot_replace", { n: slot + 1, name: name || t("skins.sticker") })
                    : t("skins.slot_add", { n: slot + 1 })
                }
                aria-pressed={on}
                onClick={() => onPick(slot)}
              >
                <span className="thumb">
                  {id ? <ItemImage src={deco?.image} alt="" /> : <FiPlus aria-hidden="true" />}
                </span>
                <span className="label">
                  {id ? name || t("skins.sticker") : t("skins.slot", { n: slot + 1 })}
                </span>
                {on && <span className="state">{t("skins.slot_active")}</span>}
              </button>
              {id && (
                <button
                  type="button"
                  className="remove"
                  disabled={busy}
                  aria-label={t("skins.slot_remove", { n: slot + 1 })}
                  title={t("skins.slot_remove", { n: slot + 1 })}
                  onClick={() => onClearSticker(slot)}
                >
                  <FiX aria-hidden="true" />
                </button>
              )}
            </Slot>
          );
        })}
        <span className="sep" aria-hidden="true" />
        <Slot $filled={!!charm} $active={active === "charm"}>
          <button
            type="button"
            className="main"
            data-slot="charm"
            disabled={busy}
            aria-label={
              charm
                ? t("skins.charm_replace", { name: decorationName(decorations[charm]) || t("skins.charm") })
                : t("skins.charm_add")
            }
            aria-pressed={active === "charm"}
            onClick={() => onPick("charm")}
          >
            <span className="thumb">
              {charm ? <ItemImage src={decorations[charm]?.image} alt="" /> : <FiImage aria-hidden="true" />}
            </span>
            <span className="label">
              {charm ? decorationName(decorations[charm]) || t("skins.charm") : t("skins.charm")}
            </span>
            {active === "charm" && <span className="state">{t("skins.slot_active")}</span>}
          </button>
          {charm && (
            <button
              type="button"
              className="remove"
              disabled={busy}
              aria-label={t("skins.charm_remove")}
              title={t("skins.charm_remove")}
              onClick={onClearCharm}
            >
              <FiX aria-hidden="true" />
            </button>
          )}
        </Slot>
      </Slots>

      <Context aria-live="polite">
        <div className="text">
          <h3>{ctxTitle}</h3>
          <div className="sub">
            {activeId ? (
              <>
                <span className="cur">
                  <ItemImage src={decorations[activeId]?.image} alt="" />
                </span>
                <span>
                  {t("skins.slot_current", { name: "" })}
                  <strong>{activeName}</strong>. {t("skins.slot_replace_hint")}
                </span>
              </>
            ) : (
              <span>
                {t("skins.slot_empty")}. {t("skins.slot_pick_hint")}
              </span>
            )}
          </div>
        </div>
        {activeId && (
          <Action
            type="button"
            className="clear"
            disabled={busy}
            onClick={() => (active === "charm" ? onClearCharm() : onClearSticker(active))}
          >
            <FiX aria-hidden="true" />
            {t("skins.slot_clear")}
          </Action>
        )}
      </Context>
    </section>
  );
}
